<script lang="ts">
import GaiaStepRail, { type GaiaStepRailItem } from './GaiaStepRail.vue';

/** GaiaStepFlow speaks the rail's item shape — the bar derives from it. */
export type GaiaStepFlowItem = GaiaStepRailItem;
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaStepBar, { type GaiaStepBarItem } from '../base/GaiaStepBar.vue';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaStepFlowItem[]>,
    required: true,
  },
  /** Key of the active step. */
  active: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{ select: [key: string] }>();

/**
 * The bar variant carries less per step (no description, count or lock glyph)
 * — on a narrow screen the step names are the whole budget. Reachability
 * still holds: a locked rail step is a disabled bar step.
 */
const barItems = computed<GaiaStepBarItem[]>(() =>
  props.items.map(item => ({
    key: item.key,
    label: item.label,
    enabled: !item.locked,
  }))
);
</script>

<template>
  <!-- One step indicator, two presentations: the vertical rail while the
       viewport is wide (WizardLayout's left column), the horizontal numbered
       bar under 1800px — where WizardLayout turns that column into a top
       strip. Both render from the same items, so the flow can't drift between
       breakpoints. -->
  <div class="gaia-step-flow">
    <gaia-step-rail
      class="flow-rail"
      :items="props.items"
      :active="props.active"
      @select="(key: string) => emit('select', key)"
    />
    <gaia-step-bar
      class="flow-bar"
      :items="barItems"
      :active="props.active"
      @select="(key: string) => emit('select', key)"
    />
  </div>
</template>

<style scoped>
.flow-bar {
  display: none;
}

/* The same viewport breakpoint as WizardLayout's rail→strip move — the strip
   and the bar have to flip together. Viewport on purpose: keying on available
   width would re-orient the whole wizard whenever the app sidebar toggles. */
@media (max-width: 1799px) {
  .flow-rail {
    display: none;
  }

  /* Centred: in the top strip the bar spans the full wizard width, and a
     left-set run of steps reads as unfinished chrome. */
  .flow-bar {
    display: flex;
    justify-content: center;
  }
}
</style>
